// Functional ingredients data for sauce personalization
// 15 total ingredients across 3 categories (5 each)

export interface FunctionalIngredient {
  id: string;
  name: string;
  category: 'beauty' | 'wellness' | 'performance';
  description: string;
  benefits: string[];
  price: number; // SGD price per add-on
  icon: string; // Emoji or icon identifier
  scientificName?: string;
  dosage?: string;
  contraindications?: string[];
}

export const FUNCTIONAL_INGREDIENTS: FunctionalIngredient[] = [
  // BEAUTY INGREDIENTS (5)
  {
    id: 'marine-collagen',
    name: 'Marine Collagen',
    category: 'beauty',
    description: 'Premium marine-sourced collagen peptides for skin elasticity and hydration',
    benefits: ['Improves skin elasticity', 'Reduces fine lines', 'Enhances hydration', 'Supports hair strength'],
    price: 1.50,
    icon: '🐟',
    scientificName: 'Hydrolyzed Marine Collagen',
    dosage: '2.5g per serving',
    contraindications: ['Fish allergies']
  },
  {
    id: 'hyaluronic-acid',
    name: 'Hyaluronic Acid',
    category: 'beauty',
    description: 'Low molecular weight hyaluronic acid for deep skin hydration',
    benefits: ['Deep skin hydration', 'Plumps skin', 'Reduces dryness', 'Anti-aging effects'],
    price: 1.20,
    icon: '💧',
    scientificName: 'Sodium Hyaluronate',
    dosage: '120mg per serving'
  },
  {
    id: 'vitamin-c-complex',
    name: 'Vitamin C Complex',
    category: 'beauty',
    description: 'Stable vitamin C with bioflavonoids for antioxidant protection',
    benefits: ['Antioxidant protection', 'Collagen synthesis', 'Brightens skin', 'Immune support'],
    price: 0.80,
    icon: '🍊',
    scientificName: 'Ascorbic Acid + Bioflavonoids',
    dosage: '500mg per serving'
  },
  {
    id: 'biotin-complex',
    name: 'Biotin Complex',
    category: 'beauty',
    description: 'High-potency biotin with B-vitamins for hair and nail health',
    benefits: ['Strengthens hair', 'Improves nail health', 'Supports metabolism', 'Energy production'],
    price: 0.60,
    icon: '💅',
    scientificName: 'D-Biotin + B-Complex',
    dosage: '2500mcg per serving'
  },
  {
    id: 'resveratrol',
    name: 'Resveratrol',
    category: 'beauty',
    description: 'Potent antioxidant from grape skin for anti-aging benefits',
    benefits: ['Anti-aging properties', 'Cellular protection', 'Skin radiance', 'Cardiovascular health'],
    price: 1.80,
    icon: '🍇',
    scientificName: 'Trans-Resveratrol',
    dosage: '100mg per serving'
  },

  // WELLNESS INGREDIENTS (5)
  {
    id: 'ashwagandha',
    name: 'Ashwagandha',
    category: 'wellness',
    description: 'Adaptogenic herb for stress management and cortisol balance',
    benefits: ['Reduces stress', 'Balances cortisol', 'Improves sleep', 'Enhances mood'],
    price: 1.00,
    icon: '🌿',
    scientificName: 'Withania somnifera',
    dosage: '300mg per serving',
    contraindications: ['Pregnancy', 'Autoimmune conditions']
  },
  {
    id: 'probiotics',
    name: 'Probiotic Blend',
    category: 'wellness',
    description: 'Multi-strain probiotic complex for digestive and immune health',
    benefits: ['Digestive health', 'Immune support', 'Gut microbiome', 'Nutrient absorption'],
    price: 1.30,
    icon: '🦠',
    scientificName: 'Lactobacillus & Bifidobacterium blend',
    dosage: '10 billion CFU per serving'
  },
  {
    id: 'turmeric-curcumin',
    name: 'Turmeric Curcumin',
    category: 'wellness',
    description: 'Standardized curcumin extract with black pepper for absorption',
    benefits: ['Anti-inflammatory', 'Joint support', 'Antioxidant', 'Digestive health'],
    price: 0.90,
    icon: '🟡',
    scientificName: 'Curcuma longa + Piperine',
    dosage: '500mg per serving'
  },
  {
    id: 'omega-3',
    name: 'Omega-3 Complex',
    category: 'wellness',
    description: 'EPA and DHA from algae for heart and brain health',
    benefits: ['Heart health', 'Brain function', 'Anti-inflammatory', 'Mood support'],
    price: 1.40,
    icon: '🐠',
    scientificName: 'Algae-derived EPA/DHA',
    dosage: '500mg per serving'
  },
  {
    id: 'magnesium-glycinate',
    name: 'Magnesium Glycinate',
    category: 'wellness',
    description: 'Highly bioavailable magnesium for muscle and nerve function',
    benefits: ['Muscle relaxation', 'Better sleep', 'Stress relief', 'Bone health'],
    price: 0.70,
    icon: '⚡',
    scientificName: 'Magnesium Bisglycinate',
    dosage: '200mg per serving'
  },

  // PERFORMANCE INGREDIENTS (5)
  {
    id: 'caffeine-natural',
    name: 'Natural Caffeine',
    category: 'performance',
    description: 'Organic caffeine from green coffee beans for clean energy',
    benefits: ['Increased energy', 'Enhanced focus', 'Fat burning', 'Athletic performance'],
    price: 0.50,
    icon: '☕',
    scientificName: 'Coffea arabica extract',
    dosage: '100mg per serving',
    contraindications: ['Caffeine sensitivity', 'Pregnancy']
  },
  {
    id: 'l-theanine',
    name: 'L-Theanine',
    category: 'performance',
    description: 'Amino acid from green tea for calm focus and mental clarity',
    benefits: ['Calm focus', 'Reduces anxiety', 'Mental clarity', 'Balances caffeine'],
    price: 0.80,
    icon: '🧠',
    scientificName: 'L-Theanine',
    dosage: '200mg per serving'
  },
  {
    id: 'creatine-monohydrate',
    name: 'Creatine Monohydrate',
    category: 'performance',
    description: 'Pure creatine for muscle strength and power output',
    benefits: ['Muscle strength', 'Power output', 'Recovery', 'Cognitive function'],
    price: 0.60,
    icon: '💪',
    scientificName: 'Creatine Monohydrate',
    dosage: '3g per serving'
  },
  {
    id: 'bcaa-complex',
    name: 'BCAA Complex',
    category: 'performance',
    description: 'Branched-chain amino acids for muscle recovery and endurance',
    benefits: ['Muscle recovery', 'Endurance', 'Protein synthesis', 'Fatigue reduction'],
    price: 1.10,
    icon: '🏃',
    scientificName: 'Leucine, Isoleucine, Valine',
    dosage: '5g per serving'
  },
  {
    id: 'rhodiola-rosea',
    name: 'Rhodiola Rosea',
    category: 'performance',
    description: 'Adaptogenic herb for physical and mental performance enhancement',
    benefits: ['Mental performance', 'Physical endurance', 'Stress adaptation', 'Fatigue resistance'],
    price: 1.20,
    icon: '🌸',
    scientificName: 'Rhodiola rosea',
    dosage: '300mg per serving'
  }
];

// Utility functions for functional ingredients
export const functionalIngredientUtils = {
  // Get all ingredients
  getAllIngredients: (): FunctionalIngredient[] => {
    return [...FUNCTIONAL_INGREDIENTS];
  },

  // Get ingredients by category
  getIngredientsByCategory: (category: 'beauty' | 'wellness' | 'performance'): FunctionalIngredient[] => {
    return FUNCTIONAL_INGREDIENTS.filter(ingredient => ingredient.category === category);
  },

  // Get ingredient by ID
  getIngredientById: (id: string): FunctionalIngredient | null => {
    return FUNCTIONAL_INGREDIENTS.find(ingredient => ingredient.id === id) || null;
  },

  // Get recommended ingredients based on quiz results
  getRecommendedIngredients: (quizProfile: any): FunctionalIngredient[] => {
    const recommendations: FunctionalIngredient[] = [];
    
    if (quizProfile.quizType === 'persona') {
      // For persona quiz, recommend based on scores
      if (quizProfile.beautyScore >= 70) {
        recommendations.push(...functionalIngredientUtils.getIngredientsByCategory('beauty').slice(0, 2));
      }
      if (quizProfile.wellnessScore >= 70) {
        recommendations.push(...functionalIngredientUtils.getIngredientsByCategory('wellness').slice(0, 2));
      }
      if (quizProfile.performanceScore >= 70) {
        recommendations.push(...functionalIngredientUtils.getIngredientsByCategory('performance').slice(0, 2));
      }
    } else {
      // For flavor quiz, recommend based on intensity and adventure scores
      if (quizProfile.intensity >= 75) {
        recommendations.push(functionalIngredientUtils.getIngredientById('caffeine-natural')!);
      }
      if (quizProfile.adventure >= 75) {
        recommendations.push(functionalIngredientUtils.getIngredientById('rhodiola-rosea')!);
      }
      // Add some general wellness ingredients
      recommendations.push(
        functionalIngredientUtils.getIngredientById('vitamin-c-complex')!,
        functionalIngredientUtils.getIngredientById('turmeric-curcumin')!
      );
    }
    
    return recommendations.filter(Boolean).slice(0, 5); // Max 5 recommendations
  },

  // Calculate total price for selected ingredients
  calculateTotalPrice: (ingredientIds: string[]): number => {
    return ingredientIds.reduce((total, id) => {
      const ingredient = functionalIngredientUtils.getIngredientById(id);
      return total + (ingredient?.price || 0);
    }, 0);
  },

  // Validate ingredient selection (max 5)
  validateSelection: (ingredientIds: string[]): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (ingredientIds.length > 5) {
      errors.push('Maximum 5 ingredients allowed');
    }
    
    // Check for invalid IDs
    const invalidIds = ingredientIds.filter(id => !functionalIngredientUtils.getIngredientById(id));
    if (invalidIds.length > 0) {
      errors.push(`Invalid ingredient IDs: ${invalidIds.join(', ')}`);
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  },

  // Get category colors for UI
  getCategoryColor: (category: 'beauty' | 'wellness' | 'performance'): string => {
    switch (category) {
      case 'beauty': return 'from-pink-500 to-rose-500';
      case 'wellness': return 'from-green-500 to-emerald-500';
      case 'performance': return 'from-blue-500 to-cyan-500';
      default: return 'from-gray-500 to-gray-600';
    }
  },

  // Get category icon
  getCategoryIcon: (category: 'beauty' | 'wellness' | 'performance'): string => {
    switch (category) {
      case 'beauty': return '💄';
      case 'wellness': return '🌿';
      case 'performance': return '⚡';
      default: return '🔬';
    }
  }
};

// Validation on module load
if (process.env.NODE_ENV === 'development') {
  console.log('✅ Functional ingredients loaded:', FUNCTIONAL_INGREDIENTS.length);
  console.log('Beauty ingredients:', functionalIngredientUtils.getIngredientsByCategory('beauty').length);
  console.log('Wellness ingredients:', functionalIngredientUtils.getIngredientsByCategory('wellness').length);
  console.log('Performance ingredients:', functionalIngredientUtils.getIngredientsByCategory('performance').length);
}